import { z } from 'zod'

/**
 * Kanonisches Statusmodell (Konzept Kap. 2, Entscheidung E-04).
 *
 * Work führt dieses schlanke Modell; GitLab-Labels und NetCon-Ticket-Stati
 * werden per konfigurierbarem Mapping darauf abgebildet – in beide Richtungen.
 *
 * Diese Liste ist die EINZIGE Wahrheit. Das Postgres-Enum wird daraus
 * abgeleitet (packages/db), nicht getrennt gepflegt.
 */
export const TASK_STATUSES = [
  'eingang',
  'bereit',
  'in_arbeit',
  'review',
  'erledigt',
  'zurueckgestellt',
  /**
   * **Ich kann nicht weiter, bis jemand antwortet**
   * ([E-315](../../../docs/ENTSCHEIDUNGEN.md#e-315), 02.09.2026).
   *
   * Aus Ticket NCW-6X848M: *„Es fehlt ein Status «Warten auf Antwort» oder so."*
   *
   * Der fachlich wertvollste Punkt des Tickets. Bis hierher hatte eine Aufgabe, die auf einen
   * Kunden wartet, zwei schlechte Plätze: in `in_arbeit` belegt sie einen Fokus-Platz und sieht
   * aus wie liegengeblieben, in `zurueckgestellt` ist sie weggelegt und kommt von selbst nie
   * zurück.
   *
   * **Steht am Ende der Liste, nicht in der Mitte.** Der Wert landet in einem MySQL-`enum`, und
   * dessen Reihenfolge ist Teil der Spaltendefinition – eine Einfügung in der Mitte
   * verschöbe die inneren Werte aller 3.000 Zeilen. Wo er im Ablauf steht, sagt
   * {@link TASK_STATUS_FLOW}; wo er in der Spalte steht, ist Buchhaltung.
   */
  'warten',
] as const

export type TaskStatus = (typeof TASK_STATUSES)[number]

export const taskStatusSchema = z.enum(TASK_STATUSES)

/** Beschriftung für die Oberfläche – deutsch, wie alle Nutzertexte. */
export const TASK_STATUS_LABELS: Record<TaskStatus, string> = {
  eingang: 'Eingang',
  bereit: 'Bereit',
  in_arbeit: 'In Arbeit',
  review: 'Review/Abnahme',
  erledigt: 'Erledigt',
  zurueckgestellt: 'Zurückgestellt',
  warten: 'Wartet auf Antwort',
}

/**
 * Regulärer Weg einer Aufgabe. `zurueckgestellt` liegt bewusst daneben:
 * von dort geht es zurück in den Zustand, aus dem die Aufgabe kam.
 */
export const TASK_STATUS_FLOW: readonly TaskStatus[] = [
  'eingang',
  'bereit',
  'in_arbeit',
  /**
   * **Zwischen Arbeit und Abnahme, weil man von dort hineingerät** (E-315): man arbeitet, stößt
   * auf eine offene Frage und wartet. Der Weg hinaus führt zurück nach `in_arbeit` – wer eine
   * Antwort hat, arbeitet weiter.
   */
  'warten',
  'review',
  'erledigt',
]

/** Zählt als offen, also noch Arbeit. */
export function isOpenStatus(status: TaskStatus): boolean {
  return status !== 'erledigt'
}

/**
 * Belegt einen Platz im persönlichen WIP-Limit (Konzept Kap. 4.3).
 *
 * **`warten` zählt nicht mit** ([E-315](../../../docs/ENTSCHEIDUNGEN.md#e-315), 02.09.2026) –
 * und das ist der ganze Sinn des Status. Wer auf eine Antwort wartet, arbeitet nicht: den Platz
 * weiter zu belegen hieße, jemanden für die Langsamkeit eines anderen zu bestrafen. Genau
 * deshalb wich man bisher auf `in_arbeit` aus und hatte drei „laufende" Aufgaben, von denen zwei
 * standen.
 *
 * `review` zählt weiterhin mit, und der Unterschied hat einen Grund: dort ist die Arbeit getan
 * und wartet auf **eine Abnahme, die Work kennt und anmahnt**. Beim Warten auf eine Antwort gibt
 * es niemanden, den Work erinnern könnte.
 */
export function countsTowardsWip(status: TaskStatus): boolean {
  return status === 'in_arbeit' || status === 'review'
}

/**
 * **Was für die Abnahme gilt – einmal formuliert, für Server und Oberfläche**
 * ([E-106](../../../docs/ENTSCHEIDUNGEN.md), erweitert am 18.08.2026).
 *
 * Nils: *„Wie signalisiert er mir, dass ich abnehmen kann? … die die keine Abnahme
 * erfordern wäre ja schön wenn er die direkt auf erledigt setzt, und die die Abnahme
 * erfordern, auf Review/Abnahme nur setzen kann."*
 *
 * Die Regel gab es serverseitig von Anfang an (`tasks.statusSetzen`), die Oberfläche kannte
 * sie nicht: sie bot dem Bearbeiter „Erledigt" an und ließ den Server den Klick abweisen.
 * Ein Knopf, der aussieht wie ein Angebot und eine Fehlermeldung ist, bringt niemandem die
 * Regel bei – er bringt ihm bei, dass das Werkzeug hakt.
 *
 * Deshalb steht sie jetzt hier und wird an **beiden** Enden benutzt. Die Serverprüfung
 * bleibt trotzdem stehen: sie ist die Zusage, das hier ist die Bequemlichkeit.
 */
export interface AbnahmeLage {
  /** Wer abnimmt – Eintrag der Aufgabe, ersatzweise der des Vorhabens. `null` = niemand. */
  abnahmeDurchId: string | null
  /** Der wirksame Bearbeiter. Wer bearbeitet hat, nimmt nicht ab. */
  assigneeId: string | null
  status: TaskStatus
}

/**
 * **Muss diese Aufgabe abgenommen werden, bevor sie erledigt ist?**
 *
 * Aus Sicht von `ichId`. Wer selbst der Abnehmer ist, hat keine Abnahmepflicht – er wartete
 * sonst auf sich selbst.
 */
export function brauchtAbnahme(lage: AbnahmeLage, ichId: string | null | undefined): boolean {
  return Boolean(lage.abnahmeDurchId) && lage.abnahmeDurchId !== ichId
}

/**
 * **Darf `ichId` diese Aufgabe jetzt auf „Erledigt" setzen?**
 *
 * Wortgleich zu `tasks.statusSetzen`: ohne Abnehmer immer; mit Abnehmer nur aus
 * „Review/Abnahme" heraus und nur von jemandem, der sie nicht selbst bearbeitet hat.
 */
export function darfErledigen(lage: AbnahmeLage, ichId: string | null | undefined): boolean {
  if (!brauchtAbnahme(lage, ichId)) return true
  return lage.status === 'review' && lage.assigneeId !== ichId
}

/** Priorität hängt an der Aufgabe und sortiert innerhalb eines Pools (Kap. 4.2). */
export const TASK_PRIORITIES = ['A', 'B', 'C'] as const
export type TaskPriority = (typeof TASK_PRIORITIES)[number]
export const taskPrioritySchema = z.enum(TASK_PRIORITIES)

/**
 * Quellsystem einer Aufgabe. Für den Bearbeiter nur ein Badge (Kap. 2).
 *
 * **`work_formular` ist eine eigene Quelle und nicht `work` mit einem Vermerk** (Phase 6).
 * Der Unterschied ist der Kanal: das Formular hat eine eigene Automatisierungsstufe, eine
 * eigene Zahl in der Kopfleiste des Eingangs und eigene Regeln. Als Vermerk an `work` wäre es
 * in jeder dieser Ansichten eine Ausnahme, die man mitdenken muss.
 */
export const TASK_SOURCES = [
  'gitlab',
  'netcon_ticket',
  'work',
  'work_formular',
  'work_mail',
  /**
   * **Gekennzeichnete Mails aus dem eigenen Outlook** ([Z-17](../../../docs/phasen/zusatz-17-outlook.md)).
   * Eine Aufgabe je Kennzeichnung, in der persönlichen Welt der Person, die sich verbunden hat.
   */
  'outlook',
  /** **Aufgaben aus Microsoft To Do** der Person – dieselbe Verbindung, eigene Marke (Z-17.9). */
  'todo',
  /**
   * **Aufgaben aus dem Notizbuch** (Z-23, [E-425](../../../docs/ENTSCHEIDUNGEN.md#e-425)): ein
   * `- [ ]` in einem neu geschriebenen Blatt oder einer Plaud-Aufnahme, oder ein KI-Fund, den
   * jemand übernommen hat. Mit Rückverweis auf die Notiz.
   */
  'notizbuch',
] as const
export type TaskSource = (typeof TASK_SOURCES)[number]
export const taskSourceSchema = z.enum(TASK_SOURCES)

/**
 * **Die Quellen, die Work selbst ist** ([E-307](../../../docs/ENTSCHEIDUNGEN.md#e-307),
 * 02.09.2026).
 *
 * Nils, nach dem ersten Entwicklertag: *„Das ist eine WORK Aufgabe, wie kann die schon
 * unterschiedliche Statis haben??? Und auch bei Quellsystem. Das ist doch work? Also das
 * eigene."*
 *
 * Er hat recht, und die Frage deckt eine Lücke im Modell auf. Jede Aufgabe führt zwei
 * Statusfelder – `status` (*was die Quelle sagt*) und `work_status` (*was Work sagt*) –, und für
 * eine importierte Aufgabe ist das genau richtig. Für eine **in Work entstandene** gibt es die
 * erste Hälfte gar nicht: niemand pflegt `status`, weil es keine Quelle gibt, die ihn pflegen
 * könnte. Trotzdem wurde er wie eine fremde Angabe behandelt – mit dem Ergebnis, dass Work einen
 * Unterschied zu sich selbst meldete.
 *
 * **Warum eine Liste und nicht `source.is_writable`.** Die Spalte sagt *„Work darf dorthin
 * zurückschreiben"* und ist heute nur zufällig deckungsgleich: sobald Phase 5 nach GitLab
 * schreibt, ist sie es nicht mehr. Die Frage hier ist eine andere – *gibt es überhaupt ein
 * Drüben?* – und die beantwortet sich aus der Quelle selbst.
 *
 * **Warum nicht ein Merkmal an `source`.** Weil es keine Einstellung ist. Eine Quelle wechselt
 * nicht die Seite; wer eine neue anlegt, weiß beim Anlegen, welche Sorte es ist. Als Spalte wäre
 * es ein Haken, den jemand falsch setzen kann, und der Fehler fiele erst an einer Meldung auf.
 *
 * Was daran hängt: der Hinweiskasten „Work führt X, die Quelle sagt Y" (entfällt), die Zeile
 * „Quellsystem" im Aufgabendetail (entfällt), und die Arbeitsliste des Rückschreibens
 * (`offeneRueckschreibungen` – nichts davon steht darin).
 */
export const EIGENE_QUELLEN: readonly TaskSource[] = [
  'work',
  'work_formular',
  'work_mail',
  'notizbuch',
]

/**
 * **Entsteht diese Aufgabe in Work selbst?** Dann gibt es kein Quellsystem, zu dem sie
 * abweichen könnte.
 *
 * Nimmt `string` und nicht `TaskSource`: `task.source_id` ist eine `varchar`-Spalte und
 * erweiterbar ohne Migration. Eine unbekannte Quelle ist keine eigene – das ist die sichere
 * Richtung, denn sie führt höchstens zu einer Meldung zu viel, nie zu einer verschwiegenen.
 */
export function istEigeneQuelle(sourceId: string | null | undefined): boolean {
  return (
    sourceId !== null && sourceId !== undefined && (EIGENE_QUELLEN as string[]).includes(sourceId)
  )
}

export const TASK_SOURCE_LABELS: Record<TaskSource, string> = {
  gitlab: 'GitLab',
  netcon_ticket: 'NetCon Ticket',
  work: 'Work',
  work_formular: 'Work-Formular',
  work_mail: 'Work-Mail',
  outlook: 'Outlook',
  todo: 'To Do',
  notizbuch: 'Notizbuch',
}

/**
 * Beschriftung **aller** Quellen in `source` – auch derer, die keine Aufgaben
 * liefern.
 *
 * `TASK_SOURCES` bleibt bewusst auf die drei aufgabenführenden Quellen
 * beschränkt: `TaskSource` ist der Typ eines Aufgaben-Badges, und dort hat das
 * Intranet nichts zu suchen. Es liefert **Personen und Abwesenheiten**, keine
 * Aufgaben (Zusatzpaket Z-01).
 *
 * Wer die Herkunft einer *Person* beschriftet – Benutzerverwaltung, Personen
 * zusammenführen –, nimmt diese Tabelle. Wer eine *Aufgabe* beschriftet, nimmt
 * `TASK_SOURCE_LABELS`.
 */
export const SOURCE_LABELS: Record<string, string> = {
  ...TASK_SOURCE_LABELS,
  intranet: 'Intranet',
}

/**
 * **Zeitfenster statt Sprints** (Konzept Kap. 4.5, Phase 8) – von Nils am 04.08.2026 geschnitten:
 * *„jetzt, innerhalb 5 Tage, innerhalb 10 Tage, Monat, Quartal."*
 *
 * ## Warum Tage und nicht „Sprint 1, Sprint 2"
 *
 * Ein Sprint ist ein Zeitraum mit Anfang, Ende und einer Zusage. Ein Zeitfenster ist eine
 * **Dringlichkeitsstufe**: „innerhalb 5 Tage" bleibt „innerhalb 5 Tage", auch wenn es drei Tage
 * später gelesen wird. Deshalb gibt es hier auch kein „verschoben" und kein „gescheitert" –
 * verschieben heißt, ein Vorhaben in ein anderes Fenster zu setzen, und das ist ein normaler
 * Vorgang (Kap. 4.5).
 *
 * ## Warum die Reihenfolge im Namen steckt
 *
 * `jetzt` < `tage5` < `tage10` < `monat` < `quartal`. Die Sortierung ist damit eine Eigenschaft
 * der Liste und keine zweite Tabelle, die jemand pflegen müsste.
 *
 * **Die Tage sind Werktage** (20.08.2026, [E-197](../../../docs/ENTSCHEIDUNGEN.md)) – gerechnet
 * wird in `zeitfensterDatum`.
 *
 * **Monat und Quartal sind Fristen, keine Kalenderzeiträume** (21.08.2026,
 * [E-200](../../../docs/ENTSCHEIDUNGEN.md)): 30 bzw. 90 Kalendertage ab der Einstufung. Als
 * „dieser Monat"/„dieses Quartal" gerechnet hielt die Leiter oben nicht – am 28. September
 * war „Quartal" zwei Tage entfernt und damit dringender als „10 Werktage".
 */
export const ZEITFENSTER = ['jetzt', 'tage5', 'tage10', 'monat', 'quartal'] as const
export type Zeitfenster = (typeof ZEITFENSTER)[number]

/**
 * **„Werktage" steht in der Langfassung, nicht auf dem Knopf** (20.08.2026,
 * [E-197](../../../docs/ENTSCHEIDUNGEN.md)): gerechnet wird in Werktagen, und die Frist
 * daneben nennt ein Datum, das vierzehn Kalendertage weiter liegen kann. Stünde dort weiter
 * „Innerhalb 10 Tage", widerspräche die Beschriftung dem Datum, das sie erklärt.
 */
export const ZEITFENSTER_LABELS: Record<Zeitfenster, string> = {
  jetzt: 'Jetzt',
  tage5: 'Innerhalb 5 Werktagen',
  tage10: 'Innerhalb 10 Werktagen',
  monat: 'Innerhalb 30 Tagen',
  quartal: 'Innerhalb 3 Monaten',
}

/**
 * **Dieselben fünf Stufen, auf Knopfbreite** (Nils, 19.08.2026: *„Das Zeitfenster am besten
 * mit Buttons/Symbolen gestalten (sieht sonst komisch aus)"*).
 *
 * Fünf Knöpfe mit „Innerhalb 10 Tage" darauf brechen in zwei Reihen um und machen aus einer
 * Nebenangabe den größten Block der Seite – das war der Grund, warum daraus ein Auswahlfeld
 * wurde. Mit `10 Tage` passen sie in eine Zeile, und damit ist der Umweg über das Aufklappen
 * überflüssig: die Stufe ist ein Klick statt drei.
 *
 * Die lange Fassung bleibt daneben stehen und wandert in `title` – sie ist die, die man beim
 * ersten Mal braucht, und der Reihe nach gelesen erklärt sie sich selbst.
 */
export const ZEITFENSTER_KURZ: Record<Zeitfenster, string> = {
  jetzt: 'Jetzt',
  tage5: '5 Tage',
  tage10: '10 Tage',
  monat: '30 Tage',
  quartal: '3 Monate',
}

/**
 * **Wie groß ist die Aufgabe?** (Nils, 17.08.2026)
 *
 * *„Ggf. müssen wir die Aufgaben bewerten können, dass ich die mit klein, mittel, groß
 * bewerten kann. Dann macht das mit der Bewertung wieder Sinn. Aufgaben ohne Bewertung sind
 * immer mittel (bzw. der Default)."*
 *
 * ## Warum drei Stufen und nicht Minuten
 *
 * `estimate_minutes` gibt es seit Phase 4. Getragen wird es von **0 von 150** offenen
 * Aufgaben ([E-78](../../../docs/ENTSCHEIDUNGEN.md)) – niemand schätzt in Minuten, weil
 * niemand weiß, ob es 90 oder 240 werden.
 *
 * *„Klein, mittel oder groß"* beantwortet dieselbe Frage in einer Sekunde, und die Antwort
 * ist ungenauer, aber vorhanden. Eine ungenaue Zahl, die es gibt, schlägt eine genaue, die
 * es nicht gibt.
 *
 * Die Minutenschätzung bleibt daneben stehen – wer sie hat, soll sie nicht verlieren.
 */
export const AUFGABEN_GROESSEN = ['klein', 'mittel', 'gross'] as const
export type Aufgabengroesse = (typeof AUFGABEN_GROESSEN)[number]
export const aufgabengroesseSchema = z.enum(AUFGABEN_GROESSEN)

export const GROESSE_LABELS: Record<Aufgabengroesse, string> = {
  klein: 'klein',
  mittel: 'mittel',
  gross: 'groß',
}

/**
 * **Die Größe, die gilt** – `mittel`, wenn niemand etwas gesagt hat.
 *
 * Von Nils so festgelegt. `null` bleibt trotzdem in der Datenbank stehen und wird nicht beim
 * Anlegen auf `mittel` gesetzt: der Unterschied zwischen *„jemand hat mittel gesagt"* und
 * *„niemand hat hingesehen"* ist die einzige Auskunft darüber, wie belastbar eine Auswertung
 * über Größen überhaupt ist. Wer ihn wegwirft, kann später nicht mehr sagen, ob 80 % mittel
 * eine Erkenntnis sind oder eine Voreinstellung.
 */
export function groesseVon(aufgabe: { groesse: string | null }): Aufgabengroesse {
  const wert = aufgabe.groesse
  return wert && (AUFGABEN_GROESSEN as readonly string[]).includes(wert)
    ? (wert as Aufgabengroesse)
    : 'mittel'
}

/**
 * **Wie lange eine Größe dauert** ([Z-19](../../../docs/phasen/zusatz-19-tagesplan.md) AP 19.1,
 * [E-373](../../../docs/ENTSCHEIDUNGEN.md#e-373); Werte **geändert am 01.10.2026**,
 * [E-390](../../../docs/ENTSCHEIDUNGEN.md#e-390)).
 *
 * Ein Tagesplan braucht für jede Zeile eine Dauer. Am 30.09.2026 nachgemessen: Von **117**
 * offenen Aufgaben trug **eine** eine Minutenschätzung. Danach zu fragen hätte nichts geändert –
 * genau das steht schon bei {@link AUFGABEN_GROESSEN}: niemand schätzt in Minuten.
 *
 * Also andersherum: Die Größe, die ohnehin gepflegt wird, bekommt eine hinterlegte Dauer. Das
 * Ergebnis ist grob, aber es existiert – und ein grober Plan, den es gibt, schlägt einen genauen,
 * den es nicht gibt.
 *
 * Die Werte sind Nils' Hausmaß (01.10.2026), nicht Wissenschaft: eine Kleinigkeit ist in einer
 * halben Stunde erledigt, etwas Mittleres kostet eine Stunde, etwas Großes einen halben Tag.
 */
export const GROESSE_MINUTEN: Record<Aufgabengroesse, number> = {
  klein: 30,
  mittel: 60,
  gross: 240,
}

/**
 * **Was eine Aufgabe ohne Größe kostet: eine Viertelstunde** (Nils, 01.10.2026:
 * *„Keine Angabe = 15min"*).
 *
 * ## Warum nicht „gilt als mittel", wie überall sonst
 *
 * {@link groesseVon} fällt bewusst auf `mittel` zurück, und das bleibt so – im Leitstand, im
 * Aktivitätsbericht und bei „Was als Nächstes" ist *„unbewertet"* eine Aufgabe wie jede andere,
 * und sie als mittelgroß zu **zählen** ist die ehrlichste Annahme.
 *
 * **Im Tagesplan ist dieselbe Annahme schädlich.** Am 30.09.2026 nachgemessen: **78 offene
 * Aufgaben** haben gar keine Größe ([F-93](../../../docs/OFFENE-FRAGEN.md#f-93)). Jede davon als
 * volle Stunde zu planen füllte den Tag mit Vermutungen und verdrängte das, was jemand wirklich
 * bewertet hat.
 *
 * Eine Viertelstunde sagt stattdessen: *„Hier steht etwas, es kostet Zeit, aber wie viel weiß
 * niemand."* Sie macht den Plan **zu optimistisch statt zu voll** – und das ist die richtige
 * Richtung: Wer merkt, dass er nicht durchkommt, setzt eine Größe. Wer einen Tag voller
 * geratener Stunden sieht, schaltet die Ansicht ab.
 */
export const OHNE_GROESSE_MINUTEN = 15

/** Die vier Stufen, für die eine Dauer hinterlegt werden kann – `ohne` heißt „nicht bewertet". */
export type Dauerstufe = Aufgabengroesse | 'ohne'

/**
 * **Was eine Arbeitswelt für die vier Stufen ansetzt** – jede Angabe freiwillig
 * ([E-390](../../../docs/ENTSCHEIDUNGEN.md#e-390)).
 *
 * Bewusst nur teilweise zu belegen: Eine Welt, die allein `gross` anders sieht, soll nicht auch
 * `klein` und `mittel` wiederholen müssen – wiederholte Werte sind die, die beim nächsten Ändern
 * auseinanderlaufen.
 */
export type Groessendauern = Partial<Record<Dauerstufe, number>>

/** Das Hausmaß für alle vier Stufen, an einer Stelle. */
export const DAUER_VORGABE: Record<Dauerstufe, number> = {
  ...GROESSE_MINUTEN,
  ohne: OHNE_GROESSE_MINUTEN,
}

/**
 * **Welche Stufe für die Dauer gilt** – anders als {@link groesseVon}.
 *
 * Hier ist *„nicht bewertet"* ein **eigener** Zustand und nicht heimlich `mittel`. Genau diese
 * Unterscheidung ist der Unterschied zwischen einem Plan, der weiß, was er nicht weiß, und einem,
 * der rät.
 */
export function dauerstufeVon(aufgabe: { groesse: string | null }): Dauerstufe {
  const wert = aufgabe.groesse
  return wert && (AUFGABEN_GROESSEN as readonly string[]).includes(wert)
    ? (wert as Aufgabengroesse)
    : 'ohne'
}

/**
 * **Die Dauer, mit der geplant wird** – in Minuten.
 *
 * Die Rangfolge ist die Belastbarkeit der Zahl:
 *
 * | | |
 * |---|---|
 * | `estimateMinutes` | **jemand hat wirklich geschätzt** – schlägt alles |
 * | `vorgabe[stufe]` | was diese Arbeitswelt für diese Stufe ansetzt |
 * | {@link DAUER_VORGABE} | das Hausmaß |
 *
 * **Nicht positive Werte zählen nicht.** Eine `0` oder eine negative Minutenzahl ist keine
 * Schätzung, sondern ein Rest aus einer Eingabemaske; sie hier durchzulassen erzeugte einen Block
 * ohne Länge, den der Planer in jede noch so kleine Lücke legte.
 */
export function dauerMinutenVon(
  aufgabe: { groesse: string | null; estimateMinutes?: number | null },
  vorgabe: Groessendauern = {},
): number {
  const geschaetzt = aufgabe.estimateMinutes
  if (typeof geschaetzt === 'number' && Number.isFinite(geschaetzt) && geschaetzt > 0) {
    return geschaetzt
  }
  const stufe = dauerstufeVon(aufgabe)
  const eigen = vorgabe[stufe]
  return typeof eigen === 'number' && Number.isFinite(eigen) && eigen > 0
    ? eigen
    : DAUER_VORGABE[stufe]
}
